import * as z from 'zod/v4';

import {
  PermissionObjectType,
  PermissionType,
} from '@watershed/constants/permissions';

// likely to be replaced a with gql enum
export enum Action {
  View = 'view',
  Edit = 'edit',
  Manage = 'manage',
  Approve = 'approve',
}

export type OrgLevelPermissionConfig = {
  permissionType: PermissionType;
};

export type ResourcePermissionConfig = {
  childResourceType?: ChildLevelResourceType;
  // gotta have at least one action
  actions: [Action, ...Array<Action>];
};

export const ORG_LEVEL_PERMISSION_TYPES = [
  PermissionType.AnyUser,
  PermissionType.WatershedAdmin,
  PermissionType.Admin,
  PermissionType.CorporateAdmin,
  PermissionType.FinanceAdmin,
  PermissionType.ManageMarketplacePurchases,
  PermissionType.ManageOrgHierarchy,
  PermissionType.ViewFootprintDetail,
  PermissionType.ViewAuditDetail,
  PermissionType.ViewReductions,
  PermissionType.ViewLearningHub,
  PermissionType.ManageSingleSignOn,
  PermissionType.ManageMeasurement,
  PermissionType.ManageReductionPlans,
  PermissionType.ManageDisclosures,
  PermissionType.ManageSuppliers,
  PermissionType.ViewCalculation,
  PermissionType.EditCalculation,
  PermissionType.ViewProductFootprint,
  PermissionType.EditProductFootprint,
] as const;

export type OrgLevelPermissionType =
  (typeof ORG_LEVEL_PERMISSION_TYPES)[number];

export type ResourceSpecificPermissionType = Exclude<
  PermissionType,
  OrgLevelPermissionType | 'ViewEmployeeReport' | 'ManageCompanyTags'
>;

export const CHILD_LEVEL_RESOURCES = [
  PermissionObjectType.Datasource,
  PermissionObjectType.ReportQuestionInstance,
] as const;

export type ChildLevelResourceType = (typeof CHILD_LEVEL_RESOURCES)[number];

export type ParentLevelResourceType = Exclude<
  PermissionObjectType,
  ChildLevelResourceType
>;

export const ADMIN_PERMISSIONS: Array<PermissionType> = [
  PermissionType.Admin,
  PermissionType.WatershedAdmin,
  PermissionType.CorporateAdmin,
  PermissionType.FinanceAdmin,
];

/**
 * NOTE: Below are tentative types that are likely to change as we implement the UX.
 */
/**
 * This is a data structure that holds all the permissions for a user or role.
 * It serves the Users and Roles pages.
 */
export type PermissionsForUserOrRole = {
  orgLevelPermissions: Array<OrgLevelPermissionType>;
  resourceLevelPermissions: {
    [key in PermissionObjectType]?: {
      [key in Action]?: {
        allowedResouceIds: { all: boolean; ids: Array<string> };
        subResourceAccess?: Record<string, Array<string>>;
      };
    };
  };
};

export type UserOrRoleId = {
  id: string;
  isRole: boolean;
};
export type OrgUsersWithResourceAccessSingle = {
  all: Array<UserOrRoleId>;
  specificIds: Map<string, Array<UserOrRoleId>>;
};
export type OrgUsersWithResourceAccessHierarchical =
  OrgUsersWithResourceAccessSingle & {
    subResourceAccess?: Map<string, OrgUsersWithResourceAccessSingle>;
  };
/**
 * This is a data structure that holds all the users and roles that
 * have any org or resource level access in a given org.
 * It serves the Permissions page.
 */
export type OrgUsersWithAccess = {
  orgLevelAccess: Map<OrgLevelPermissionType, Array<UserOrRoleId>>;
  resourceLevelAccess: Map<
    PermissionObjectType,
    OrgUsersWithResourceAccessHierarchical
  >;
};

export const MAX_NUM_ROLES_OR_USERS_TO_DISPLAY_FOR_RESOURCE = 4;

export const usersAndRolesWithPermissions = z.array(
  z.object({
    permissionInput: z.object({
      permission: z.nativeEnum(PermissionType),
      objectType: z.nativeEnum(PermissionObjectType).nullable(),
      objectId: z.string().nullable(),
    }),
    usersAndRoles: z.object({
      // we cap at 5 users or roles to limit the amount of data we pass over the wire
      examples: z
        .array(
          z.object({
            id: z.string(),
            name: z.string(),
            type: z.enum(['user', 'role']),
          })
        )
        .max(MAX_NUM_ROLES_OR_USERS_TO_DISPLAY_FOR_RESOURCE),
      total: z.number(),
    }),
  })
);

export type UsersAndRolesWithPermissions = z.infer<
  typeof usersAndRolesWithPermissions
>;

// These are the things we allow users to sort by in the permission resource list
export const PermissionResourceListSortModel = ['userOrRoleAccess'];
